import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { WEEKDAYS_MODEL, WEEKDAYS_SHORT_MODEL } from '../../../../Apis/functions';
import { MoonIcon, SunIcon, SunriseIcon, CafeteriaIcon } from '../../../../components/buttons/IconButtons';
import { CustomCardGrid } from '../../../../components/CustomCardGrid';
import CustomChip from '../../../../components/CustomChip';
import CustomTooltip from '../../../../components/CustomTooltip';
import { colors } from '../../../../enums/colors';
import SelectCoffeeShopApp from '../Functionality/SelectCoffeShopApp';
import i18next from 'i18next';
import React from "react";

/**
 *
 * /**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * CoffeeShopCards component
 *
 * Displays a list of coffee shop cards with schedule information,
 * current open/closed status, and related icons for meal availability.
 *
 * @component
 * @param {Object[]} data - Array of coffee shop objects with `id`, `name`, and `schedule` info.
 * @param {string} [className=""] - Optional custom class to apply to the container.
 * @param {Function} [onClick=() => {}] - Function to call when a card is clicked.
 * @param elementRef
 */
const CoffeeShopCards = ({ data, className = "", onClick = () => { } ,elementRef}) => {
    const { t } = useTranslation()
    const currentLang = i18next.language ? i18next.language : "en";
    const { getCafeteriaData } = SelectCoffeeShopApp()

    //Get weekdays abbreviations by current language
    const WEEKDAYS_SHORT = WEEKDAYS_SHORT_MODEL[currentLang];


    /**
     * Handles card click event.
     * Calls the external onClick function and updates the selected cafeteria in state.
     * @param cafeteria
     */
    const handleClick = (cafeteria) => {
        onClick()
        getCafeteriaData(cafeteria)
    }

    return (
        <div className='panelTables' ref={elementRef}>
            <div className={`row ${className}`}>
                {data.map((cafeteria) => {

                    // Get current date and time
                    const now = moment(); // current moment
                    const currentDayIndex = (now.day() === 0) ? 6 : now.day() - 1; // Monday=0, Sunday=6

                    // Current time formatted as "HH:mm:ss"
                    const currentTime = now.format('HH:mm:ss');

                    // Get the available days of the cafeteria
                    const availableDays = WEEKDAYS_MODEL.reduce((acc, dayName, idx) => {
                        if (cafeteria.schedule?.[dayName]) acc.push(idx);
                        return acc;
                    }, []);

                    const openTime = cafeteria.schedule?.openTime;
                    const closeTime = cafeteria.schedule?.closeTime;

                    // Validate if the cafeteria is open today
                    const isOpenToday = cafeteria.schedule?.[WEEKDAYS_MODEL[currentDayIndex]];

                    // Validate if it is currently open
                    const isOpen =
                        isOpenToday &&
                        openTime &&
                        closeTime &&
                        currentTime >= openTime &&
                        currentTime <= closeTime;


                    return (
                        <CustomCardGrid
                            key={cafeteria.id}
                            isCheckboxShow={false}
                            itemId={cafeteria.id}
                            checked={false}
                            onCheckboxChange={onClick}
                            disabled={false}
                            statusColor={colors.secondary100}
                            fontColor={colors.secondary700}
                            borderColor={colors.secondary150}
                            showCustomChip={false}
                            srcImage={<CafeteriaIcon />}
                            isDefaultIcon
                            isSquareImg={true}
                            classNamePanelGrid='col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 px-1 mb-3 position-relative'
                            onClick={() => handleClick(cafeteria)}
                            height='coffee-shop-container'
                        >
                            <CustomChip
                                background={colors.success100}
                                borderColor={colors.success150}
                                legend={isOpen ? t("open") : t("closed")}
                                className='position-absolute r-16px t-16px'
                                fontColor={colors.success700}
                            />
                            <div className='d-flex justify-content-between flex-grow-1 minw-0 align-items-center'>
                                <div className="fs-12px ibl-font-w-600 font-color-general-950 mr-2">{t('name')}</div>
                                <div className={"flex-grow-1 minw-0 text-right"}>
                                    <CustomTooltip
                                        text={cafeteria.name}
                                        classes={`fs-14px font-color-general-950 text-ellipsis-1-row justify-content-end`}
                                        showIfTruncated={true}
                                    />
                                </div>
                            </div>
                            <div className="gap-12px mb-2 py-3 d-flex align-items-center justify-content-center mb-2">
                                {WEEKDAYS_SHORT.map((day, i) => (
                                    <span
                                        key={i}
                                        className={`day-circle w-h-24px rounded-circle small font-weight-bold d-flex justify-content-center align-items-center ${availableDays.includes(i) ? 'active' : 'inactive'}`}>
                                    {day}
                                </span>
                                ))}
                            </div>

                            <div className="d-flex gap-3 justify-content-center mt-2">
                                <CustomTooltip text={t('breakfast')}>
                                    <SunriseIcon />
                                </CustomTooltip>
                                <CustomTooltip text={t('lunch')}>
                                    <SunIcon />
                                </CustomTooltip>
                                <CustomTooltip text={t('dinner')}>
                                    <MoonIcon />
                                </CustomTooltip>
                            </div>
                            <div>
                                <div className='d-flex justify-content-between my-4'>
                                    <p className='fs-12px ibl-font-w-600 font-color-general-950 pt-2'>{t('open_time')}</p>
                                    <p className='fs-14px pt-2 pr-2'>{openTime}</p>
                                </div>
                                <div className='d-flex justify-content-between my-4'>
                                    <p className='fs-12px ibl-font-w-600 font-color-general-950 pr-2'>{t('close_time')}</p>
                                    <p className='fs-14px pr-2'>{closeTime}</p>
                                </div>
                            </div>
                        </CustomCardGrid>
                    );
                })}
            </div>
        </div>
    );
};

export default CoffeeShopCards;
